import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { MessageCircle, Send, Image as ImageIcon, Edit2, Trash2, Reply, ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function CommunityDetail({ user, onLogout }) {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postContent, setPostContent] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    fetchData();
  }, [communityId]);

  const fetchData = async () => {
    try {
      const [communityRes, postsRes] = await Promise.all([
        axios.get(`${API}/communities/${communityId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/communities/${communityId}/posts`, { headers: getAuthHeaders() })
      ]);
      setCommunity(communityRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load community');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large — max 5 MB');
      return;
    }

    setPostImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/communities/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return res.data.image_url;
    } catch (error) {
      throw new Error('Failed to upload image');
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    if (!postContent.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setPosting(true);
    try {
      let imageUrl = '';
      if (postImage) {
        setUploading(true);
        imageUrl = await uploadImage(postImage);
        setUploading(false);
      }

      await axios.post(`${API}/communities/${communityId}/posts`, {
        community_id: communityId,
        content: postContent,
        image_url: imageUrl || null
      }, { headers: getAuthHeaders() });

      toast.success('Post submitted — teacher will reply soon');
      setPostContent('');
      setPostImage(null);
      setImagePreview('');
      fetchData();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      await axios.post(`${API}/communities/${communityId}/posts`, {
        community_id: communityId,
        content: replyContent,
        parent_post_id: postId
      }, { headers: getAuthHeaders() });

      toast.success('Reply posted');
      setReplyingTo(null);
      setReplyContent('');
      fetchData();
    } catch (error) {
      toast.error('Failed to post reply');
    }
  };

  const handleEditPost = async (postId) => {
    if (!editContent.trim()) {
      toast.error('Content cannot be empty');
      return;
    }

    try {
      await axios.put(`${API}/communities/posts/${postId}`, {
        content: editContent
      }, { headers: getAuthHeaders() });

      toast.success('Post updated');
      setEditingPost(null);
      setEditContent('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/communities/posts/${postId}`, { headers: getAuthHeaders() });
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="community">
        <div className="p-8">Loading...</div>
      </Layout>
    );
  }

  if (!community) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="community">
        <div className="p-8">Community not found</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="community">
      <div className="p-6 lg:p-8 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/community')}
          className="text-primary-blue hover:text-[#1874CD] font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
          Back to Communities
        </Button>

        {/* Community Header */}
        <Card className="border-0 card-shadow bg-white rounded-2xl overflow-hidden">
          {community.hero_image_url && (
            <div className="h-48 overflow-hidden">
              <img 
                src={`${BACKEND_URL}${community.hero_image_url}`} 
                alt={community.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardContent className="p-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2">{community.title}</h1>
            <p className="text-primary-blue font-medium mb-3">{community.course_title}</p>
            {community.details && (
              <p className="text-[#4D4D4D] mb-3">{community.details}</p>
            )}
            {community.tags && community.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {community.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 bg-blue-50 text-primary-blue text-xs rounded-md">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Post Box */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-navy mb-4">Ask your doubt — teachers will answer!</h3>
            <form onSubmit={handleCreatePost} className="space-y-4">
              <Textarea
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                placeholder="Share your thoughts, ask a question, or post a doubt..."
                rows={4}
                className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
              />
              
              {imagePreview && (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPostImage(null);
                      setImagePreview('');
                    }}
                    className="absolute top-2 right-2 bg-white hover:bg-gray-100 rounded-full"
                  >
                    ×
                  </Button>
                </div>
              )}
              
              <div className="flex space-x-3">
                <label className="cursor-pointer">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <div className="flex items-center space-x-2 px-4 py-2 border-2 border-[#E7E7E7] rounded-xl hover:border-primary-blue transition-smooth cursor-pointer">
                    <ImageIcon className="w-4 h-4 text-[#4D4D4D]" strokeWidth={2} />
                    <span className="text-sm font-medium text-[#4D4D4D]">Add Image</span>
                  </div>
                </label>
                
                <Button
                  type="submit"
                  disabled={posting || uploading || !postContent.trim()}
                  className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press"
                >
                  {posting || uploading ? 'Posting...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" strokeWidth={2} />
                      Post
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <Card className="border-0 card-shadow bg-white rounded-2xl">
              <CardContent className="p-12 text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                <p className="text-[#4D4D4D]">No posts yet. Be the first to start a discussion!</p>
              </CardContent>
            </Card>
          ) : (
            posts.map((post) => (
              <Card key={post.id} className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-6">
                  {/* Post Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {/* FIXED: Display profile photo */}
                      {post.user_profile_photo ? (
                        <div className="relative">
                          <img
                            src={`${BACKEND_URL}${post.user_profile_photo}`}
                            alt={post.user_name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                          />
                          {post.user_profile_verified && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          post.user_role === 'teacher' || post.user_role === 'admin'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-primary-blue'
                        }`}>
                          <span className="text-lg font-bold">
                            {post.user_name?.charAt(0) || 'U'}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-bold text-navy">{post.user_name}</p>
                          {(post.user_role === 'teacher' || post.user_role === 'admin') && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                              Mentor
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#A5A5A5]">{formatDate(post.created_at)}</p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {(user.id === post.user_id || user.role === 'teacher' || user.role === 'admin') && (
                      <div className="flex space-x-2">
                        {user.id === post.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingPost(post.id);
                              setEditContent(post.content);
                            }}
                            className="text-primary-blue hover:bg-blue-50 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4" strokeWidth={2} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePost(post.id)}
                          className="text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Post Content */}
                  {editingPost === post.id ? (
                    <div className="space-y-3 mb-4">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={3}
                        className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleEditPost(post.id)}
                          className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl"
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPost(null);
                            setEditContent('');
                          }}
                          className="border-2 border-[#E7E7E7] rounded-xl"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#4D4D4D] mb-3 whitespace-pre-wrap">{post.content}</p>
                      {post.image_url && (
                        <img 
                          src={`${BACKEND_URL}${post.image_url}`} 
                          alt="Post"
                          className="w-full max-h-96 object-contain rounded-xl mb-3"
                        />
                      )}
                      {post.is_edited && (
                        <p className="text-xs text-[#A5A5A5] mb-3">(edited)</p>
                      )}
                    </>
                  )}
                  
                  {/* Reply Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReplyingTo(replyingTo === post.id ? null : post.id)}
                    className="text-primary-blue hover:bg-blue-50 rounded-lg"
                  >
                    <Reply className="w-4 h-4 mr-1" strokeWidth={2} />
                    Reply ({post.reply_count || 0})
                  </Button>
                  
                  {/* Reply Form */}
                  {replyingTo === post.id && (
                    <div className="mt-4 pl-4 border-l-2 border-blue-200 space-y-2">
                      <Textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write your reply..."
                        rows={2}
                        className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleReply(post.id)}
                          className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl"
                        >
                          Reply
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyContent('');
                          }}
                          className="border-2 border-[#E7E7E7] rounded-xl"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Replies */}
                  {post.replies && post.replies.length > 0 && (
                    <div className="mt-4 space-y-3 pl-4 border-l-2 border-gray-200">
                      {post.replies.map((reply) => (
                        <div key={reply.id} className="bg-[#F7F7F7] rounded-xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {/* FIXED: Display profile photo for replies */}
                              {reply.user_profile_photo ? (
                                <div className="relative">
                                  <img
                                    src={`${BACKEND_URL}${reply.user_profile_photo}`}
                                    alt={reply.user_name}
                                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                  />
                                  {reply.user_profile_verified && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                                      <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                                  reply.user_role === 'teacher' || reply.user_role === 'admin'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-primary-blue'
                                }`}>
                                  <span className="font-bold">
                                    {reply.user_name?.charAt(0) || 'U'}
                                  </span>
                                </div>
                              )}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-semibold text-sm text-navy">{reply.user_name}</p>
                                  {(reply.user_role === 'teacher' || reply.user_role === 'admin') && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                      Mentor
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-[#A5A5A5]">{formatDate(reply.created_at)}</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-[#4D4D4D] whitespace-pre-wrap">{reply.content}</p>
                          {reply.image_url && (
                            <img 
                              src={`${BACKEND_URL}${reply.image_url}`} 
                              alt="Reply"
                              className="w-full max-h-64 object-contain rounded-xl mt-2"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}